// Özel lig sayfasının (Junior Cup) üst menüsü.
//
// Sitenin üst menüsünden tamamen ayrı bir tasarımdır. Yönetim > Junior Cup
// ekranından düzenlenir:
//   • Renkler   — theme.menu (şerit, menü zemini, yazı, seçili öğe, düğme)
//   • Öğeler    — layout.header.items (görünürlük, ad, sıra) + özel bağlantılar
//   • Düzen     — layout.header.style ("bar" | "centered"), sabit menü, giriş düğmeleri
//   • Lig/sezon seçici — layout.showScopePicker
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser } from "../UserContext";
import { routeAfterLogin } from "../hooks/useLogin";
import { isManagementRole } from "../utils/roleManagement";
import { portalMenuHref, portalPath } from "./portalPaths";
import { menuStyleVars, portalAssetUrl } from "./portalAssets";
import "./PortalChrome.css";

const idOf = (option) =>
  option && typeof option === "object" ? String(option.id ?? option.value ?? "").trim() : String(option ?? "").trim();
const labelOf = (option) =>
  option && typeof option === "object" ? String(option.label ?? option.name ?? option.id ?? "") : String(option ?? "");

/** Menüde gösterilecek öğeler: yerleşik sayfalar + özel bağlantılar. */
export function buildPortalMenu(portal, scope = {}) {
  const header = portal?.layout?.header || {};
  const items = (header.items || [])
    .filter((item) => item.visible !== false)
    .map((item) => ({ key: item.key, label: item.label, to: portalMenuHref(portal, item.key, scope), external: false }))
    .filter((item) => item.to);
  const links = (header.links || [])
    .filter((link) => link.visible !== false)
    .map((link, index) => ({
      key: `link-${index}`,
      label: link.label,
      to: link.url,
      external: /^https?:/i.test(link.url),
    }));
  return [...items, ...links];
}

function MenuLink({ item, active, onClick, className }) {
  if (item.external) {
    return (
      <a className={className} href={item.to} target="_blank" rel="noreferrer" onClick={onClick}>
        {item.label}
      </a>
    );
  }
  return (
    <Link className={`${className}${active ? " is-active" : ""}`} to={item.to} onClick={onClick} aria-current={active ? "page" : undefined}>
      {item.label}
    </Link>
  );
}

export default function PortalHeader({
  portal,
  leagueOptions = [],
  seasonOptions = [],
  selectedCity = "",
  selectedLeague = "",
  selectedSeason = "",
  onLeagueChange,
  onSeasonChange,
  showFilters = true,
  isDarkMode = false,
  onToggleTheme,
}) {
  const location = useLocation();
  const { user } = useUser() || {};
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const header = portal.layout?.header || {};
  const scope = { cityId: idOf(selectedCity), leagueId: idOf(selectedLeague), seasonId: idOf(selectedSeason) };
  const menu = useMemo(() => buildPortalMenu(portal, scope), [portal, scope.cityId, scope.leagueId, scope.seasonId]); // eslint-disable-line react-hooks/exhaustive-deps
  const homeTo = portalPath(portal, scope.leagueId, scope.seasonId, "anasayfa") || `/${portal.slug}`;
  const logo = portalAssetUrl(portal.logoUrl);
  const name = portal.shortName || portal.name;

  const leagues = leagueOptions.map((o) => ({ id: idOf(o), label: labelOf(o) })).filter((o) => o.id);
  const seasons = seasonOptions.map((o) => ({ id: idOf(o), label: labelOf(o) })).filter((o) => o.id);
  const showPicker = showFilters && portal.layout?.showScopePicker !== false && (leagues.length > 0 || seasons.length > 0);
  const showLogin = header.showLogin !== false;
  const canToggleTheme = portal.theme?.mode === "auto" && typeof onToggleTheme === "function";

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isActive = (item) => !item.external && location.pathname === item.to.split("?")[0];

  const picker = showPicker ? (
    <div className="pm-picker" role="group" aria-label="Lig ve sezon seçimi">
      {leagues.length > 1 && (
        <label>
          <span>Lig</span>
          <select value={scope.leagueId} onChange={(e) => onLeagueChange?.(e.target.value)}>
            {leagues.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {seasons.length > 0 && (
        <label>
          <span>Sezon</span>
          <select value={scope.seasonId} onChange={(e) => onSeasonChange?.(e.target.value)}>
            {seasons.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  ) : null;

  const auth = showLogin ? (
    <div className="pm-auth">
      {user ? (
        <Link to={routeAfterLogin(user)} className="pm-btn pm-btn--primary">
          {isManagementRole(user.role) ? "Yönetim Paneli" : "Panelim"}
        </Link>
      ) : (
        <>
          <Link to="/giris" className="pm-btn pm-btn--ghost">
            Giriş Yap
          </Link>
          <Link to="/kayit-ol" className="pm-btn pm-btn--primary">
            Kayıt Ol
          </Link>
        </>
      )}
    </div>
  ) : null;

  return (
    <header
      className={[
        "pm-header",
        `pm-header--${header.style === "centered" ? "centered" : "bar"}`,
        header.sticky !== false ? "is-sticky" : "",
        scrolled ? "is-scrolled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={menuStyleVars(portal)}
    >
      {(picker || auth || canToggleTheme) && (
        <div className="pm-topbar">
          <div className="pm-wrap pm-topbar__inner">
            {picker || <span />}
            <div className="pm-topbar__end">
              {canToggleTheme && (
                <button type="button" className="pm-icon-btn" onClick={onToggleTheme} aria-label="Açık / koyu görünüm">
                  <i className={`fa-solid ${isDarkMode ? "fa-sun" : "fa-moon"}`} />
                </button>
              )}
              {auth}
            </div>
          </div>
        </div>
      )}

      <div className="pm-bar">
        <div className="pm-wrap pm-bar__inner">
          <Link to={homeTo} className="pm-brand" aria-label={`${name} anasayfa`}>
            {logo ? <img src={logo} alt="" /> : <span className="pm-brand__mark">{name.charAt(0)}</span>}
            <span className="pm-brand__name">{name}</span>
          </Link>

          <nav className="pm-nav" aria-label={`${name} menüsü`}>
            {menu.map((item) => (
              <MenuLink key={item.key} item={item} active={isActive(item)} className="pm-nav__item" />
            ))}
          </nav>

          <button
            type="button"
            className="pm-burger"
            onClick={() => setOpen(true)}
            aria-label="Menüyü aç"
            aria-expanded={open}
            aria-controls="pm-drawer"
          >
            <i className="fa-solid fa-bars" />
          </button>
        </div>
      </div>

      {open && (
        <div className="pm-drawer-backdrop" onClick={() => setOpen(false)} role="presentation">
          <div
            id="pm-drawer"
            className="pm-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Menü"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="pm-drawer__head">
              <span className="pm-brand">
                {logo ? <img src={logo} alt="" /> : <span className="pm-brand__mark">{name.charAt(0)}</span>}
                <span className="pm-brand__name">{name}</span>
              </span>
              <button type="button" className="pm-icon-btn" onClick={() => setOpen(false)} aria-label="Menüyü kapat">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <nav className="pm-drawer__nav">
              {menu.map((item) => (
                <MenuLink key={item.key} item={item} active={isActive(item)} className="pm-drawer__item" onClick={() => setOpen(false)} />
              ))}
            </nav>
            {picker && <div className="pm-drawer__picker">{picker}</div>}
            {auth}
          </div>
        </div>
      )}
    </header>
  );
}
